/**
 * When a pane's poll may ask again (`useFetch`'s `refreshMs`).
 *
 * A poll's period is the staleness the pane accepts: a pane polled every
 * minute asks at most once a minute, whatever else happens to the page. Until
 * 2026-10-27 two things asked again without looking at how old the data was.
 * Polling turning back on (the tab shown again, the open bell) asked at once,
 * and it re-armed the poll's timer with a new random offset. On 2026-10-27 an
 * open Live Breadth page on the S&P 1500 asked production for its whole series
 * (9,200 days, 806 KB) and its last 60 days as a pair, sent together, about
 * every 1.9 s through the session: 11,400 of each, about 9.3 GB, where the
 * design asks for the whole series every ten minutes and the 60 days every
 * minute. A page hidden for a moment every 1.9 s gives exactly that in a
 * browser (scripts/proof-breadth-series-pace.ts), and only those two poll on
 * that page, which is why nothing else looped in the log.
 *
 * So a poll asks only when what the pane holds was asked for a period ago, and
 * polling turning on asks at once only when the data is that old or was asked
 * for before the session now open (the bell). The period runs from the last
 * ask, whatever made it (a tick, the mount, an explicit refetch).
 *
 * Pure: no fetch, no DOM. The clock is the caller's.
 */
import { marketState } from "@/lib/marketHours";

/** A regular session is 6.5 hours; two asks in it are less than that apart. */
const SESSION_MS = 6.5 * 3_600_000;

/**
 * How early a poll may be and still be on time: a timer can fire a few
 * milliseconds early, and the ask it compares against was made a moment after
 * its own tick. A tenth of the period, and never more than a quarter second.
 */
export function pollSlackMs(periodMs: number): number {
  return Math.min(250, periodMs / 10);
}

/**
 * May a poll ask now? Yes when nothing was asked yet, or when what is held
 * was asked for at least one period ago (less the slack).
 */
export function pollDue(askedAt: number | null, now: number, periodMs: number): boolean {
  if (askedAt == null) return true;
  return now - askedAt >= periodMs - pollSlackMs(periodMs);
}

/**
 * Polling has just turned on again (the page shown again, the open bell, a
 * period that was paused): ask at once? Yes when a poll is due, or when it is
 * the regular session by the clock and what is held was asked for before this
 * session opened: outside the regular session, or in another one. Otherwise
 * the poll's own next tick asks. The session is the clock's, not the page's
 * `useMarketOpen`: that one is told of the close up to 30 s late, and in those
 * seconds every ask made after 16:00 would have read as before the session.
 */
export function catchUpDue(askedAt: number | null, now: number, periodMs: number): boolean {
  if (pollDue(askedAt, now, periodMs)) return true;
  if (askedAt == null || marketState(now) !== "rth") return false;
  const sameSession = marketState(askedAt) === "rth" && now - askedAt < SESSION_MS;
  return !sameSession;
}
